"use client"

import { BIKE_TYPES } from "@/constants/admin-paths"
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons"
import { Button, Form, Input, message } from "antd"
import Title from "antd/es/typography/Title"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState } from "react"
import { t } from "../../../../languages/i18n"
import { createBikeType, getBikeTypeById, updateBikeType } from "../services/bike_type"

const UpsertBikeType: React.FC = () => {
  const [form] = Form.useForm()
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const [loading, setLoading] = useState(false)
  const router = useRouter()

  const normalizeValue = (name: string): string => {
    if (!name) return ""
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[đĐ]/g, "d")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .trim()
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nameValue = e.target.value
    const value = normalizeValue(nameValue)
    form.setFieldsValue({ value })
  }

  const handleFetchBikeType = useCallback(
    async (id: number) => {
      try {
        setLoading(true)
        const response = await getBikeTypeById(id)

        if (response) {
          form.setFieldsValue({
            name: response.data.attributes.name,
            value: response.data.attributes.slug || normalizeValue(response.data.attributes.name),
          })
        }
        setLoading(false)
      } catch {
        message.error(t("Failed to fetch bike type"))
        setLoading(false)
      }
    },
    [form],
  )

  useEffect(() => {
    if (id) {
      handleFetchBikeType(Number(id))
    }
  }, [id, handleFetchBikeType])

  const handleSubmit = async () => {
    try {
      const values = form.getFieldsValue()
      const payload = {
        name: values.name,
        value: values.value,
      }

      if (id) {
        await updateBikeType(id, payload)
        message.success(t("Bike type updated successfully"))
        router.push(`/admin${BIKE_TYPES.LIST.PATH}`)
      } else {
        await createBikeType(payload)
        message.success(t("Bike type added successfully"))
        form.resetFields()
        router.push(`/admin${BIKE_TYPES.LIST.PATH}`)
      }
    } catch {
      message.error(t(id ? "Failed to update bike type" : "Failed to add bike type"))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ width: "90%", float: "left" }} className="ml-4">
      <Title level={2} className="mt-5">
        {t(id ? "Edit Bike Type" : "Add New Bike Type")}
      </Title>

      <Form
        labelAlign="left"
        labelCol={{ span: 5 }}
        form={form}
        onFinish={handleSubmit}
        className="mt-7"
        style={{ maxWidth: 800 }}
      >
        <div className="flex flex-col gap-4">
          <Form.Item
            style={{ marginBottom: 20, width: "100%" }}
            name="name"
            label={<span className="text-base font-medium">{t("Name")}</span>}
            rules={[{ required: true, message: t("Minimum 1 characters!"), min: 1 }]}
            extra={t("The name is how it appears on your site.")}
          >
            <Input style={{ width: "100%" }} onChange={handleNameChange} />
          </Form.Item>

          <Form.Item
            style={{ marginBottom: 20 }}
            name="value"
            label={<span className="text-base font-medium">{t("Value")}</span>}
            rules={[{ required: true, message: t("Minimum 1 characters!"), min: 1 }]}
            extra={t(
              "The value is automatically generated from the name (e.g., 'E-bike' → 'e-bike', 'Tag along' → 'tag-along')",
            )}
          >
            <Input />
          </Form.Item>

          <Form.Item className="mt-6">
            <div className="flex">
              <Button
                icon={<CheckCircleOutlined />}
                style={{ marginRight: "20px" }}
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                {t(id ? "Update Bike Type" : "Add Bike Type")}
              </Button>
              <Button
                icon={<CloseCircleOutlined />}
                danger
                onClick={() => router.push(`/admin${BIKE_TYPES.LIST.PATH}`)}
              >
                {t("Cancel")}
              </Button>
            </div>
          </Form.Item>
        </div>
      </Form>
    </div>
  )
}

export default UpsertBikeType
